// Start session (qa-runs 1162): the DM's button at the table. It records the session's start (a table event the session
// recap reads) and, when the campaign's Discord is connected with Session starting on, posts the start with the table's
// link. Pressed again, it asks first: "Posted at 7:02 pm. Post again?". The webhook itself never reaches this page.
import { useEffect, useState } from 'react';
import { requestJson } from '../api';
import { Modal } from './Dialogs';
import { BTN_MD, PRIMARY } from './ui';

interface Status { connected: boolean; on: boolean; dead: boolean; lastStart: string | null }

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export function StartSessionDialog({ campaignId, onClose, onSaid, fetchImpl = fetch }: { campaignId: string; onClose: () => void; onSaid: (text: string) => void; fetchImpl?: typeof fetch }) {
  const [status, setStatus] = useState<Status | null | false>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    void requestJson(`/api/tables/${campaignId}/session-start`, { method: 'GET' }, fetchImpl).then(({ status: code, body }) => {
      if (live) setStatus(code === 200 && body && typeof body === 'object' ? body as Status : false);
    });
    return () => { live = false; };
  }, [campaignId, fetchImpl]);

  const start = async () => {
    setBusy(true);
    const { status: code, body } = await requestJson(`/api/tables/${campaignId}/session-start`, { method: 'POST', body: '{}' }, fetchImpl);
    setBusy(false);
    const b = (body ?? {}) as { posted?: boolean; connected?: boolean; dead?: boolean };
    if (code !== 201) onSaid('The session could not be started just now.');
    else if (b.posted) onSaid('The session has started. The group\'s Discord was told.');
    else if (b.dead) onSaid('The session has started. The Discord link stopped working, so nothing was posted: paste a new link in the campaign\'s settings.');
    else onSaid('The session has started.');
    onClose();
  };

  if (status === null) return null;
  const posts = !!status && status.connected && status.on && !status.dead;
  const again = status && status.lastStart && Date.now() - new Date(status.lastStart).getTime() < 12 * 3600_000 ? status.lastStart : null;
  return (
    <Modal title={again ? (posts ? `Posted at ${timeOf(again)}. Post again?` : `Started at ${timeOf(again)}. Start again?`) : 'Start the session?'} onClose={onClose} testId="start-session-dialog">
      <div className="space-y-3 text-sm text-stone-300">
        {status && status.dead && (
          <p role="alert" className="text-amber-300" data-testid="start-session-dead">
            The Discord link stopped working (it may have been deleted in Discord), so nothing will be posted. Paste a new link in the campaign&apos;s settings.
          </p>
        )}
        <p>{posts
          ? 'The group\'s Discord gets: the session is starting, with a link to this table. Players still sign in and need a seat.'
          : status && status.connected && !status.on
            ? 'Session starting posts are switched off for this campaign, so nothing is posted.'
            : 'The table notes the session\'s start. No Discord channel is connected; the DM can connect one in the campaign\'s settings.'}</p>
        <div className="flex justify-end gap-2">
          <button type="button" className={BTN_MD} style={{ minHeight: 44 }} onClick={onClose}>Cancel</button>
          <button type="button" className={PRIMARY} style={{ minHeight: 44 }} disabled={busy} onClick={() => { void start(); }} data-testid="start-session-go">
            {again ? (posts ? 'Post again' : 'Start again') : 'Start session'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
